import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Table, Form, Card, Spinner, Alert, Button } from 'react-bootstrap';
import api from '../../api/axios'; // Use centralized api instance
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

const ManagePrices = () => {
    const { user } = useContext(AuthContext);
    const [sizes, setSizes] = useState([]);
    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [prices, setPrices] = useState({});
    const [initialPrices, setInitialPrices] = useState({});
    const [samePriceToggles, setSamePriceToggles] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const canManagePrices = hasPermission(user?.role, 'settings:prices');

    const fetchAllData = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            const [sizesRes, menusRes, optionsRes, pricesRes] = await Promise.all([
                api.get('/vehiclesizes'),
                api.get('/servicemenus'),
                api.get('/options'),
                api.get('/prices')
            ]);

            setSizes(sizesRes.data.data.sizes);
            setMenus(menusRes.data.data.filter(m => m.isActive));
            setOptions(optionsRes.data.data.filter(o => o.isActive));

            const priceMap = {};
            pricesRes.data.data.servicePrices.forEach(p => {
                priceMap[`service_${p.menu_id}_size_${p.size_id}`] = p.price;
            });
            pricesRes.data.data.optionPrices.forEach(p => {
                priceMap[`option_${p.option_id}_size_${p.size_id}`] = p.price;
            });
            setPrices(priceMap);
            setInitialPrices(priceMap);

        } catch (err) {
            setError('데이터를 불러오는 중 오류가 발생했습니다.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchAllData();
        }
    }, [user, fetchAllData]);

    const handlePriceChange = (value, type, itemId, sizeId) => {
        const toggleKey = `${type}_${itemId}`;
        const isSamePrice = samePriceToggles[toggleKey];
        let newPrices = { ...prices };

        if (isSamePrice) {
            sizes.forEach(s => {
                const key = `${type}_${itemId}_size_${s.id}`;
                newPrices[key] = value;
            });
        } else {
            const key = `${type}_${itemId}_size_${sizeId}`;
            newPrices[key] = value;
        }
        setPrices(newPrices);
    };

    const handleSamePriceToggle = (type, itemId) => {
        const toggleKey = `${type}_${itemId}`;
        const newToggles = { ...samePriceToggles, [toggleKey]: !samePriceToggles[toggleKey] };
        setSamePriceToggles(newToggles);

        if (newToggles[toggleKey] && sizes.length > 0) {
            const firstSizeId = sizes[0].id;
            const key = `${type}_${itemId}_size_${firstSizeId}`;
            const firstPrice = prices[key] || '';
            
            let newPrices = { ...prices };
            sizes.forEach(s => {
                newPrices[`${type}_${itemId}_size_${s.id}`] = firstPrice;
            });
            setPrices(newPrices);
        }
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        const promises = [];
        
        for (const key in prices) {
            if (prices[key] !== initialPrices[key]) {
                const [type, itemId, , sizeId] = key.split('_');
                const price = prices[key];
                
                if (price === null || price === '') continue; // Do not save empty prices

                const url = type === 'service' ? '/prices/service' : '/prices/option';
                const payload = type === 'service' 
                    ? { menu_id: itemId, size_id: sizeId, price }
                    : { option_id: itemId, size_id: sizeId, price };
                
                promises.push(api.post(url, payload));
            }
        }

        try {
            await Promise.all(promises);
            setInitialPrices(prices);
        } catch (err) {
            console.error("Failed to save prices:", err);
            setError('가격 저장 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;

    const renderPriceInput = (type, item) => {
        return sizes.map((size, index) => {
            const key = `${type}_${item.id}_size_${size.id}`;
            const toggleKey = `${type}_${item.id}`;
            const isSamePrice = samePriceToggles[toggleKey];
            const isDisabled = (isSamePrice && index > 0) || !canManagePrices;
            const itemName = type === 'service' ? item.menuName : item.optionName;

            return (
                <td key={key}>
                    <Form.Control
                        id={key}
                        name={key}
                        type="number"
                        value={prices[key] || ''}
                        onChange={(e) => handlePriceChange(e.target.value, type, item.id, size.id)}
                        placeholder="가격 입력"
                        disabled={isDisabled}
                        aria-label={`${itemName} - ${size.sizeName} 가격`}
                    />
                </td>
            );
        });
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>가격표</h3>
                    <p>메뉴와 사이즈별 가격을 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    {canManagePrices && (
                        <Button variant="primary" size="sm" onClick={handleSaveAll} disabled={isSaving}>
                            {isSaving ? <Spinner as="span" animation="border" size="sm" /> : '전체 저장'}
                        </Button>
                    )}
                </div>
            </div>
            <Card>
                <Card.Header>
                    <Card.Title className="mb-0">가격 매트릭스</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Table responsive bordered>
                        <thead>
                            <tr>
                                <th>메뉴 / 옵션</th>
                                {sizes.map(size => <th key={size.id} className="text-center">{size.sizeName}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-secondary"><td colSpan={sizes.length + 1}><strong>기본 메뉴</strong></td></tr>
                            {menus.map(menu => (
                                <tr key={`service-${menu.id}`}>
                                    <td>
                                        <Form.Check 
                                            type="checkbox"
                                            id={`same-price-service-${menu.id}`}
                                            label={menu.menuName}
                                            checked={!!samePriceToggles[`service_${menu.id}`]}
                                            onChange={() => handleSamePriceToggle('service', menu.id)}
                                            disabled={!canManagePrices}
                                        />
                                    </td>
                                    {renderPriceInput('service', menu)}
                                </tr>
                            ))}
                            <tr className="table-secondary"><td colSpan={sizes.length + 1}><strong>옵션</strong></td></tr>
                            {options.map(option => (
                                <tr key={`option-${option.id}`}>
                                    <td>
                                        <Form.Check 
                                            type="checkbox"
                                            id={`same-price-option-${option.id}`}
                                            label={`${option.optionName} (${option.type})`}
                                            checked={!!samePriceToggles[`option_${option.id}`]}
                                            onChange={() => handleSamePriceToggle('option', option.id)}
                                            disabled={!canManagePrices}
                                        />
                                    </td>
                                    {renderPriceInput('option', option)}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    );
};
export default ManagePrices;
